/**
 * usePageTracking — Auto-track page views on route changes.
 *
 * Add this hook to your root router component (e.g. MainRouterNew).
 * It will emit a GA4 page_view event every time the route changes.
 *
 * Usage:
 *   import { usePageTracking } from 'hooks/usePageTracking';
 *
 *   function MainRouterNew() {
 *     usePageTracking();
 *     return <Routes>...</Routes>;
 *   }
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ga4Analytics } from 'services/analytics.service';

/**
 * Maps route paths to human-readable page names.
 * Add new routes here as they are created.
 */
const ROUTE_PAGE_MAP: Record<string, string> = {
  '/': 'Home',
  '/control-room': 'Control Room',
  '/fleet-overview': 'Fleet Overview',
  '/utilization': 'Utilization',
  '/production': 'Production',
  '/fuel-analytics': 'Fuel Analytics',
  '/fuel-usage': 'Fuel Usage',
  '/tire-management': 'Tire Management',
  '/tire-pressure': 'Tire Pressure',
  '/tpms-live': 'TPMS Live',
  '/driver-behavior': 'Driver Behavior',
  '/engine-hours': 'Engine Hours',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/fleet-comparison': 'Fleet Comparison',
  '/fleet-planner': 'Fleet Planner',
  '/insights': 'Insights',
  '/maintenance': 'Maintenance',
  '/network-health': 'Network Health',
  '/road-condition': 'Road Condition',
  '/customer-selection': 'Customer Selection',
  '/notifications': 'Notifications',
  '/work-orders': 'Work Orders',
};

export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const pageName =
      ROUTE_PAGE_MAP[location.pathname] || formatPathToTitle(location.pathname);
    ga4Analytics.trackPageView(pageName, location.pathname);
  }, [location.pathname]);
}

/**
 * Fallback: convert a route path like '/driver-behavior' to 'Driver Behavior'.
 */
function formatPathToTitle(path: string): string {
  return (
    path
      .replace(/^\//, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown'
  );
}
